import dayjs from 'dayjs';

export const getDisplayDateLabel = (date?: string, fallback?: string) => {
  if (!date && !fallback) {
    return null;
  }

  const parsedDate = date ? dayjs(date) : dayjs(fallback);

  if (!parsedDate.isValid()) {
    return null;
  }

  const today = dayjs().startOf('day');
  const tomorrow = today.add(1, 'day');

  if (parsedDate.isSame(today, 'day')) {
    return 'Today';
  }

  if (parsedDate.isSame(tomorrow, 'day')) {
    return 'Tomorrow';
  }

  return parsedDate.format('MMM D');
};

/**
 * Extracts a numeric millisecond timestamp for ordering suggested blocks.
 * Supports full ISO datetimes, separate date + time, or falls back to time on today.
 */
export const getSuggestedEventTimestamp = (event: {
  startTime?: string;
  date?: string;
  before?: {
    startTime?: string;
    date?: string;
  };
}): number => {
  const startTime = event.startTime || event.before?.startTime;
  const date = event.date || event.before?.date;
  if (startTime && (startTime.includes('-') || startTime.includes('T'))) {
    const parsed = dayjs(startTime);
    if (parsed.isValid()) {
      return parsed.valueOf();
    }
  }

  const effectiveDate = date || dayjs().format('YYYY-MM-DD');
  if (startTime) {
    const match = startTime.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      const [, hours, minutes] = match;
      const parsed = dayjs(effectiveDate)
        .hour(Number(hours))
        .minute(Number(minutes))
        .second(0)
        .millisecond(0);
      if (parsed.isValid()) {
        return parsed.valueOf();
      }
    }
    const combined = dayjs(`${effectiveDate} ${startTime}`);
    if (combined.isValid()) {
      return combined.valueOf();
    }
  }

  if (date) {
    const parsedDate = dayjs(date).startOf('day');
    if (parsedDate.isValid()) {
      return parsedDate.valueOf();
    }
  }

  return 0;
};

/**
 * Sorts suggested blocks chronologically by date and start time so earlier dates
 * (e.g. Today) appear at the top, followed by Tomorrow and subsequent days.
 */
export const sortSuggestedBlocksByDate = <
  T extends {
    startTime?: string;
    date?: string;
    before?: { startTime?: string; date?: string };
  }
>(
  blocks: T[]
): T[] => {
  return [...blocks].sort((a, b) => getSuggestedEventTimestamp(a) - getSuggestedEventTimestamp(b));
};

/** Format a duration in minutes as "1h 30m" / "2h" / "45m" — mirrors RN
 * AssistantChatSuggestedRoutineBlock / TaskBlock duration labels. */
export const formatDurationLabel = (totalMinutes?: number | null): string | null => {
  if (totalMinutes == null || !Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return null;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
};
